"use client";

import type { CarePlanCycleDetail } from "@/types/care-plan";

import { CarePlanContext } from "./care-plan";
import {
  type CarePlanSize,
  type CarePlanStep,
  type DetailMode,
  type Orientation,
} from "./types";
import { isPatientStep } from "./types";
import { useCarePlan } from "./use-care-plan";
import { useCarePlanContextValue } from "./use-care-plan-context-value";
import { usePatientCarePlan } from "./use-patient-care-plan";

interface CarePlanPreviewProps {
  steps: CarePlanStep[];
  orientation?: Orientation;
  detailMode?: DetailMode;
  enableMessages?: boolean;
  size?: CarePlanSize;
  cycles?: CarePlanCycleDetail[];
  children: React.ReactNode;
}

/**
 * Lightweight read-only provider for previewing care plan steps.
 * No mutations, no editor, no cycles. Just displays step cards with lookups.
 *
 * Used by: template picker, template list sample, audit trail.
 */
export function CarePlanPreview({
  steps,
  orientation = "horizontal",
  detailMode = "popover",
  enableMessages = true,
  size = "md",
  cycles,
  children,
}: CarePlanPreviewProps) {
  const isPatient = steps.length > 0 && isPatientStep(steps[0]);

  if (isPatient) {
    return (
      <PatientPreviewInner
        steps={steps}
        orientation={orientation}
        detailMode={detailMode}
        enableMessages={enableMessages}
        size={size}
        cycles={cycles}
      >
        {children}
      </PatientPreviewInner>
    );
  }

  return (
    <TemplatePreviewInner
      steps={steps}
      orientation={orientation}
      detailMode={detailMode}
      enableMessages={enableMessages}
      size={size}
    >
      {children}
    </TemplatePreviewInner>
  );
}

function PatientPreviewInner({
  steps,
  orientation,
  detailMode,
  enableMessages,
  size,
  cycles,
  children,
}: Omit<CarePlanPreviewProps, "children"> & { children: React.ReactNode }) {
  const {
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    ...actions
  } = usePatientCarePlan(
    steps as import("./types").PatientCarePlanStep[],
    undefined,
    undefined,
    cycles ? { cycles } : undefined,
  );

  const value = useCarePlanContextValue({
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    actions,
    orientation: orientation!,
    detailMode: detailMode!,
    readOnly: true,
    isCreating: false,
    enableMessages: enableMessages!,
    size: size!,
    cycles,
  });

  return (
    <CarePlanContext.Provider value={value}>
      {children}
    </CarePlanContext.Provider>
  );
}

function TemplatePreviewInner({
  steps,
  orientation,
  detailMode,
  enableMessages,
  size,
  children,
}: Omit<CarePlanPreviewProps, "children"> & { children: React.ReactNode }) {
  const {
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    ...actions
  } = useCarePlan(steps, undefined);

  const value = useCarePlanContextValue({
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    actions,
    orientation: orientation!,
    detailMode: detailMode!,
    readOnly: true,
    isCreating: false,
    enableMessages: enableMessages!,
    size: size!,
  });

  return (
    <CarePlanContext.Provider value={value}>
      {children}
    </CarePlanContext.Provider>
  );
}
